// Madde metni içinde, başlık listesindeki maddeleri tespit edip tıklanabilir
// hale getirmek için yardımcı fonksiyonlar.
//
// Tasarım notları:
// - Eşleşme tam kelime/ifade bazlıdır (Türkçe harfler dahil sınır kontrolü).
//   Örn. "Şam" maddesi "yaşamak" kelimesinin içinde etiketlenmez.
// - Başlıklardaki sondaki parantezli ekler (tarih, Osmanlıca yazım vb.)
//   ayıklanır. Örn. "A. Tunalı Hilmi Bey (1871-1928)" -> "A. Tunalı Hilmi Bey".
// - Matcher (regex + harita) bir kez üretilip cache'lenir, tekrar tekrar
//   kurulmaz; böylece sayfa yavaşlamaz.

const MIN_MATCH_LENGTH = 3;

// Başlık sonundaki parantezli ekleri (tarih, Osmanlıca, açıklama) temizle.
export const stripTitleForMatch = (title) => {
  if (!title) return "";
  let t = String(title).trim();
  // Sondaki " (...)" gruplarını sırayla kaldır.
  let prev;
  do {
    prev = t;
    t = t.replace(/\s*\([^()]*\)\s*$/u, "").trim();
  } while (t !== prev);
  return t;
};

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

let cachedMatcher = null;
let cachedKey = null;

// titleList: [{ id, title, definition, primaryImage }]
export const buildTitleMatcher = (titleList) => {
  if (!Array.isArray(titleList) || titleList.length === 0) return null;
  if (cachedMatcher && cachedKey === titleList) return cachedMatcher;

  const byName = new Map();
  const byId = new Map();
  const phrases = [];

  for (const it of titleList) {
    const clean = stripTitleForMatch(it.title);
    if (clean.length < MIN_MATCH_LENGTH) continue;

    const data = {
      id: it.id,
      title: clean,
      fullTitle: it.title,
      definition: it.definition || "",
      primaryImage: it.primaryImage || "",
    };

    const key = clean.toLocaleLowerCase("tr-TR");
    if (!byName.has(key)) {
      byName.set(key, data);
      phrases.push(clean);
    }
    if (!byId.has(it.id)) byId.set(it.id, data);
  }

  if (phrases.length === 0) return null;

  // Uzun ifadeler önce eşleşsin (greedy).
  phrases.sort((a, b) => b.length - a.length);

  const alternation = phrases.map(escapeRegExp).join("|");
  let regex;
  try {
    // Türkçe duyarlı sınır: önünde/ardında harf, rakam veya birleştirici işaret olmamalı.
    regex = new RegExp(
      `(?<![\\p{L}\\p{N}\\p{M}])(?:${alternation})(?![\\p{L}\\p{N}\\p{M}])`,
      "giu"
    );
  } catch {
    // Lookbehind desteklemeyen eski tarayıcılar için yedek.
    regex = new RegExp(`\\b(?:${alternation})\\b`, "giu");
  }

  cachedMatcher = { regex, byName, byId };
  cachedKey = titleList;
  return cachedMatcher;
};

const SKIP_TAGS = new Set([
  "A",
  "SCRIPT",
  "STYLE",
  "BUTTON",
  "CODE",
  "PRE",
  "TEXTAREA",
]);

// root altındaki metin düğümlerinde eşleşen başlıkları <span.item-link> ile sarar.
// Idempotent'tir: zaten işaretlenmiş düğümlere tekrar dokunmaz.
export const linkifyContainer = (root, matcher, currentItemId) => {
  if (!root || !matcher) return;
  const { regex, byName } = matcher;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || !node.nodeValue.trim())
        return NodeFilter.FILTER_REJECT;
      let p = node.parentElement;
      while (p && p !== root) {
        if (
          SKIP_TAGS.has(p.tagName) ||
          (p.classList && p.classList.contains("item-link"))
        ) {
          return NodeFilter.FILTER_REJECT;
        }
        p = p.parentElement;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const textNodes = [];
  let n;
  while ((n = walker.nextNode())) textNodes.push(n);

  for (const textNode of textNodes) {
    const text = textNode.nodeValue;
    regex.lastIndex = 0;
    const matches = [...text.matchAll(regex)];
    if (!matches.length) continue;

    const frag = document.createDocumentFragment();
    let last = 0;
    let linked = false;

    for (const m of matches) {
      const matched = m[0];
      const start = m.index;
      const key = matched.toLocaleLowerCase("tr-TR");
      const data = byName.get(key);
      if (!data || data.id === currentItemId) continue;

      if (start > last)
        frag.appendChild(document.createTextNode(text.slice(last, start)));

      const span = document.createElement("span");
      span.className = "item-link";
      span.setAttribute("role", "link");
      span.setAttribute("tabindex", "0");
      span.dataset.itemId = String(data.id);
      span.textContent = matched;
      frag.appendChild(span);

      last = start + matched.length;
      linked = true;
    }

    if (!linked) continue;
    if (last < text.length)
      frag.appendChild(document.createTextNode(text.slice(last)));
    textNode.parentNode.replaceChild(frag, textNode);
  }
};
